import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MoreHorizontal } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { useToast } from "@/components/toast";
import { MemberActionsDialog } from "@/components/member-actions";
import { Button, EmptyState, ErrorState, Loading, PageHeader, Pill } from "@/components/ui";

const STATUS_TONE = { pending: "rose", accepted: "success", declined: "neutral", cancelled: "neutral", expired: "neutral" };

function RequestItem({ r, box, busy, act, onMore }) {
  const navigate = useNavigate();
  const open = () => r.other && navigate(`/member/${r.other.id}`);
  return (
    <li className="fade-up rounded-[20px] border border-border bg-white p-4 shadow-sm" data-testid={`request-item-${r.id}`}>
      <div className="flex items-center gap-3">
        <button data-testid={`request-open-profile-${r.id}`} onClick={open} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <img src={photoUri(r.other?.main_photo_url)} alt="" className="h-16 w-16 shrink-0 rounded-full bg-[#F4EBE6] object-cover" />
          <span className="min-w-0">
            <span className="block truncate font-display text-[22px] leading-tight">{r.other?.display_name}, {r.other?.age}</span>
            <span className="block truncate text-sm text-muted-foreground">{r.type_label} · {r.other?.city}</span>
            <span className="block text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
          </span>
        </button>
        <Pill label={r.status[0].toUpperCase() + r.status.slice(1)} tone={STATUS_TONE[r.status]} testId={`request-status-${r.id}`} />
        {box === "received" ? (
          <button data-testid={`request-more-${r.id}`} aria-label="More actions" onClick={() => onMore(r)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-[#F4EBE6]"><MoreHorizontal className="h-5 w-5" /></button>
        ) : null}
      </div>
      {r.status === "pending" ? (
        <div className="mt-4 flex gap-2">
          {box === "received" ? (
            <>
              <Button testId={`request-decline-${r.id}`} size="sm" variant="outline" title="Decline" loading={busy === `${r.id}-decline`} className="flex-1" onClick={() => act(r, "decline")} />
              <Button testId={`request-view-${r.id}`} size="sm" variant="rose" title="View Profile" className="flex-1" onClick={open} />
              <Button testId={`request-accept-${r.id}`} size="sm" title="Accept" loading={busy === `${r.id}-accept`} className="flex-1" onClick={() => act(r, "accept")} />
            </>
          ) : (
            <Button testId={`request-withdraw-${r.id}`} size="sm" variant="outline" title="Withdraw request" loading={busy === `${r.id}-withdraw`} className="flex-1" onClick={() => act(r, "withdraw")} />
          )}
        </div>
      ) : null}
    </li>
  );
}

export default function Requests() {
  const [box, setBox] = useState("received");
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [more, setMore] = useState(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["requests", box], queryFn: () => api(`/requests?box=${box}`) });

  const act = async (r, action) => {
    setBusy(`${r.id}-${action}`);
    try {
      await api(`/requests/${r.id}/${action}`, { method: "POST" });
      const name = r.other?.display_name ?? "member";
      toast(action === "accept" ? `You are now connected with ${name}` : action === "decline" ? "Request declined" : "Request withdrawn", action === "accept" ? "success" : "info");
      ["requests", "unread", "connections", "matches", "notifications"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (action === "accept") navigate("/connections");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="requests-screen" className="mx-auto max-w-3xl">
      <PageHeader title="Requests" />
      <div className="mb-6 inline-flex rounded-full bg-[#F4EBE6] p-1" role="tablist">
        {["received", "sent"].map((b) => (
          <button key={b} role="tab" aria-selected={box === b} data-testid={`requests-tab-${b}`} onClick={() => setBox(b)}
            className={`h-10 rounded-full px-6 text-sm font-bold transition-colors ${box === b ? "bg-primary text-white" : "text-foreground hover:bg-white/60"}`}>
            {b === "received" ? "Received" : "Sent"}
          </button>
        ))}
      </div>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="requests-error" message={error.message} onRetry={() => refetch()} /> : !data?.requests.length ? (
        <EmptyState testId="requests-empty" icon={Mail} title="No requests yet." text={box === "received" ? "When members send you a request, it will appear here." : "Requests you send from Discover appear here."} />
      ) : (
        <ul className="flex flex-col gap-3">{data.requests.map((r) => <RequestItem key={r.id} r={r} box={box} busy={busy} act={act} onMore={setMore} />)}</ul>
      )}
      <MemberActionsDialog member={more?.other ?? null} onClose={() => setMore(null)} />
    </div>
  );
}
